import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Block, CancelOutlined, CheckCircleOutline, Edit, LockOpen } from '@mui/icons-material';
import TelegramIcon from '@mui/icons-material/Telegram';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { adminUsersService, AdminExpertUser, AdminExpertDetail, TelegramStatus, AdminPlanOption } from '@services/admin-users.service.ts';
import { ConnectionsByStatusChips } from '@components/admin/ConnectionsByStatusChips.tsx';
import { toastService } from '@services/toast.service.ts';
import { AdminExpertDetailModal } from '@components/admin/AdminExpertDetailModal.tsx';
import { AdminChangePlanModal } from '@components/admin/AdminChangePlanModal.tsx';
import { UserStatusChip } from '@components/admin/UserStatusChip.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';

const TelegramStatusChip: React.FC<{ status: TelegramStatus }> = ({ status }) => {
  switch (status) {
    case 'active':
      return (
        <Tooltip title="Telegram підключено">
          <TelegramIcon color="primary" sx={{ fontSize: 20 }} />
        </Tooltip>
      );
    case 'pending':
      return (
        <Tooltip title="Очікує підключення Telegram">
          <TelegramIcon color="warning" sx={{ fontSize: 20 }} />
        </Tooltip>
      );
    case 'blocked':
      return (
        <Tooltip title="Telegram заблоковано">
          <TelegramIcon color="error" sx={{ fontSize: 20 }} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Telegram не підключено">
          <TelegramIcon color="disabled" sx={{ fontSize: 20, opacity: 0.5 }} />
        </Tooltip>
      );
  }
};

const AdminExpertsPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [experts, setExperts] = useState<AdminExpertUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [selectedExpert, setSelectedExpert] = useState<AdminExpertDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [planOptions, setPlanOptions] = useState<AdminPlanOption[]>([]);
  const [planModalExpert, setPlanModalExpert] = useState<AdminExpertUser | null>(null);
  const [statusConfirmExpert, setStatusConfirmExpert] = useState<AdminExpertUser | null>(null);

  const fetchExperts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminUsersService.getExperts({
        page,
        limit,
        search: searchQuery || undefined,
        planCode: planFilter || undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
      });
      setExperts(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список експертів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, planFilter, statusFilter]);

  const handleConfirmStatusToggle = async () => {
    if (!statusConfirmExpert) return;
    const newStatus = statusConfirmExpert.status === 1 ? 2 : 1;
    try {
      await adminUsersService.setUserStatus('experts', statusConfirmExpert.id, newStatus);
      setExperts((prev) => prev.map((e) => (e.id === statusConfirmExpert.id ? { ...e, status: newStatus } : e)));
      toastService.success(newStatus === 2 ? 'Експерта заблоковано.' : 'Експерта розблоковано.');
    } catch {
      toastService.error('Не вдалося змінити статус експерта.');
    } finally {
      setStatusConfirmExpert(null);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  useEffect(() => {
    adminUsersService.getExpertPaymentsOptions().then((opts) => setPlanOptions(opts.plans));
  }, []);

  const handleRowClick = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const detail = await adminUsersService.getExpertById(id);
      setSelectedExpert(detail);
    } catch {
      toastService.error('Не вдалося завантажити деталі експерта.');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setTimeout(() => setSelectedExpert(null), 300);
  };

  const handlePlanChange = async (planId: number) => {
    if (!planModalExpert) return;
    await adminUsersService.setExpertPlan(planModalExpert.id, planId);
    const plan = planOptions.find((p) => p.value === planId);
    setExperts((prev) => prev.map((e) =>
      e.id === planModalExpert.id
        ? { ...e, subscription: plan ? { planName: plan.label, planCode: plan.code, status: 'active' } : e.subscription }
        : e,
    ));
    toastService.success('Тарифний план змінено.');
  };

  return (
    <Box data-i-doc={PageIndices.ADMIN_EXPERTS}>
      <PageHint iDoc={PageIndices.ADMIN_EXPERTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-experts"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'planCode',
              type: 'select',
              label: 'Тариф',
              value: planFilter,
              onChange: (v) => { setPlanFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                ...planOptions.map((p) => ({ value: p.code, label: p.label })),
              ],
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => { setStatusFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: '1', label: 'Активний' },
                { value: '2', label: 'Заблокований' },
                { value: '3', label: 'Видалений' },
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setPlanFilter(''); setStatusFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : experts.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Експертів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Аватар</TableCell>
                  <TableCell>TG</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Часовий пояс</TableCell>
                  <TableCell>Рейтинг</TableCell>
                  <TableCell>Спеціалізації</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Клієнтів</TableCell>
                  <TableCell>Реєстрація</TableCell>
                  <TableCell align="right">Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {experts.map((expert) => {
                  return (
                    <TableRow
                      hover
                      key={expert.id}
                      onClick={() => handleRowClick(expert.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <UserAvatar path={expert.avatar} name={expert.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>
                        <TelegramStatusChip status={expert.telegramStatus} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{expert.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{expert.email}</Typography>
                          {expert.isEmailVerified ? (
                            <Tooltip title="Email верифіковано">
                              <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email не верифіковано">
                              <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {expert.phone && (
                          <Typography variant="caption" color="text.secondary">{expert.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>{expert.timezone ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating value={expert.rating} precision={0.1} readOnly size="small" />
                          <Typography variant="body2" color="text.secondary">
                            {expert.rating != null ? expert.rating.toFixed(1) : '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {expert.specializations.length > 0
                            ? expert.specializations.map((s) => (
                                <Chip key={s} label={s} size="small" variant="outlined" />
                              ))
                            : '—'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <UserStatusChip status={expert.status} isProtectedDemoAccount={expert.isProtectedDemoAccount} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PlanChip subscription={expert.subscription} />
                          <Tooltip title="Змінити план">
                            <IconButton size="small" onClick={() => setPlanModalExpert(expert)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell><ConnectionsByStatusChips connectionsByStatus={expert.connectionsByStatus} /></TableCell>
                      <TableCell>{new Date(expert.createdAt).toLocaleDateString('uk-UA')}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {expert.status === 1 && (
                          <Tooltip title="Заблокувати">
                            <IconButton size="small" color="error" onClick={() => setStatusConfirmExpert(expert)}>
                              <Block fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {expert.status === 2 && (
                          <Tooltip title="Розблокувати">
                            <IconButton size="small" color="success" onClick={() => setStatusConfirmExpert(expert)}>
                              <LockOpen fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-experts"
        />
      </PageCard>

      <AdminExpertDetailModal
        expert={detailLoading ? null : selectedExpert}
        open={detailOpen}
        onClose={handleDetailClose}
        onStatusChanged={(id, status) => {
          setExperts((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
          setSelectedExpert((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
        }}
      />

      <AdminChangePlanModal
        open={!!planModalExpert}
        onClose={() => setPlanModalExpert(null)}
        onSave={handlePlanChange}
        plans={planOptions}
        currentPlanCode={planModalExpert?.subscription?.planCode ?? null}
        userName={planModalExpert?.name ?? ''}
      />

      <ConfirmationDialog
        open={!!statusConfirmExpert}
        onClose={() => setStatusConfirmExpert(null)}
        onConfirm={handleConfirmStatusToggle}
        title={statusConfirmExpert?.status === 1 ? 'Заблокувати експерта?' : 'Розблокувати експерта?'}
        message={
          statusConfirmExpert?.status === 1
            ? `${statusConfirmExpert?.name} втратить доступ до застосунку. Дані не видаляються — дію можна скасувати.`
            : `${statusConfirmExpert?.name} знову зможе увійти в застосунок.`
        }
        confirmText={statusConfirmExpert?.status === 1 ? 'Заблокувати' : 'Розблокувати'}
      />
    </Box>
  );
};

export const AdminExpertsPage: React.FC = () => (
  <DashboardLayout>
    <AdminExpertsPageContent />
  </DashboardLayout>
);
